import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { userGroupsService } from '@modules/userGroups/services/userGroupsService';
import { userService } from '../services/userService';
import useFormHook from '@shared/hooks/useFormHook';

export const useUserGroupsList = (onClose, onSave, groupData) => {
    const { t } = useTranslation();
    const [allGroups, setAllGroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    
    const { modalRef } = useFormHook({}, onClose);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null); 
            
            const [groupsResponse, userData] = await Promise.all([
                userGroupsService.allUserGroups(),
                userService.getUserById(groupData)
            ]);

            setAllGroups(groupsResponse);
            if (userData?.groups) {
                setSelectedGroups(userData.groups.map((group) => (
                    typeof group === 'object' && group !== null ? group.id : group
                )));
            }
        } catch (err) {
            setError(t('userGroups.error.loading'));
            console.error('Error fetching groups data:', err);
        } finally {
            setLoading(false);
        }
    }, [groupData, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleGroupToggle = useCallback((groupId) => {
        setSelectedGroups(prev => 
            prev.includes(groupId)
                ? prev.filter(id => id !== groupId)
                : [...prev, groupId]
        );
    }, []);

    const handleSave = useCallback(async () => {
        try {
            setSaving(true);
            setError(null); 
            await onSave(selectedGroups);
            onClose();
        } catch (error) {
            setError(t('userGroups.error.saving'));
            console.error('Failed to save groups:', error);
        } finally {
            setSaving(false);
        }
    }, [selectedGroups, onSave, onClose, t]);

    return {
        allGroups,
        selectedGroups,
        loading,
        saving,
        error,
        modalRef,
        handleGroupToggle,
        handleSave
    };
}; 